import { Component, inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { FormsModule, FormControl, Validators, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DataService } from '../../services/data.service';
import { MaterialModule } from '../../../../../../../modules/catch-core/third-party/material/material.module';
import { ColorPickerModule } from 'ngx-color-picker';

interface TargetField {
  id?: string;
  name: string;
  fieldType: string;
  value?: {
    options?: any[];
  };
}

interface ShapeData {
  name: string;
}

interface ColorDataItem {
  value: string;
  color: string;
}

interface EditDialogData {
  data: {
    catchindex: number;
    bindingName: string;
    targetField: TargetField;
    shape: ShapeData;
    defaultColor: string;
    colorData: ColorDataItem[];
  };
}

interface TableColumnDataRow {
  catchindex: number;
  bindingName: string;
  targetField: TargetField;
  shape: ShapeData;
  defaultColor: string;
  colorData: ColorDataItem[];
}

@Component({
  selector: 'app-edit-dialog',
  templateUrl: './edit-dialog.component.html',
  styleUrls: ['./edit-dialog.component.css'],
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MaterialModule, ColorPickerModule],
})
export class EditDialogComponent implements OnInit {
  // Inject dependencies using Angular 20 inject() function
  public dialogRef = inject(MatDialogRef<EditDialogComponent>);
  public editData = inject<EditDialogData>(MAT_DIALOG_DATA);
  private dataService = inject(DataService);

  // Form controls
  public formControl = new FormControl('', [Validators.required]);
  public form!: FormGroup;

  // Component properties
  public columnConfigurations: any[] = [];
  public bindingName = '';
  public targetField: TargetField = { fieldType: 'default', name: '' };
  public selectedShape = '';
  public defaultColor = '';
  public catchIndex: number | null = null;
  public color = '#296A88'; // Helper for color picker

  // Lists for dropdowns
  public shapeList: any[] = [];
  public fieldList: TargetField[] = [];
  public colorData: ColorDataItem[] = [];

  // Original data for comparison
  private originalData: TableColumnDataRow | null = null;

  ngOnInit(): void {
    this.initializeComponent();
    this.setupFormValidation();
  }

  /**
   * Initialize component with data from dialog
   */
  private initializeComponent(): void {
    try {
      this.columnConfigurations = this.dataService.getTableConfiguration();
      this.fieldList = this.columnConfigurations[1]?.['options'] || [];
      this.shapeList = this.columnConfigurations[2]?.['options'] || [];

      if (this.editData?.data) {
        const data = this.editData.data;
        this.bindingName = data.bindingName || '';
        this.targetField = data.targetField || { fieldType: 'default', name: '' };
        this.selectedShape = data.shape?.name || '';
        this.defaultColor = data.defaultColor || '#296A88';
        this.colorData = data.colorData ? [...data.colorData] : [];
        this.catchIndex = data.catchindex ?? null;

        // Store original data for comparison
        this.originalData = { ...data };
      }
    } catch (error) {
      console.error('Error initializing edit dialog:', error);
    }
  }

  /**
   * Setup form validation
   */
  private setupFormValidation(): void {
    this.form = new FormGroup({
      bindingName: new FormControl(this.bindingName, [Validators.required]),
      targetField: new FormControl(this.targetField, [Validators.required]),
      selectedShape: new FormControl(this.selectedShape, [Validators.required]),
      defaultColor: new FormControl(this.defaultColor, [Validators.required]),
    });
  }

  /**
   * Handle target field selection change
   */
  public onTargetFieldSelection(selectedField: TargetField): void {
    this.targetField = selectedField;

    // Update color data based on field type
    if (this.targetField?.fieldType === 'Checkbox') {
      // Initialize checkbox color data if not already set
      if (!this.colorData || this.colorData.length === 0) {
        this.colorData = [
          { value: 'True', color: this.defaultColor },
          { value: 'False', color: this.defaultColor },
        ];
      }
    } else if (this.targetField?.fieldType === 'DropdownSingle') {
      const dropdownOptions = this.targetField.value?.options;
      if (dropdownOptions && Array.isArray(dropdownOptions)) {
        // Only reinitialize if options changed
        const currentValues = this.colorData.map((item) => item.value);
        const newValues = dropdownOptions.map((opt) => opt.toString());

        if (JSON.stringify(currentValues) !== JSON.stringify(newValues)) {
          this.colorData = dropdownOptions.map((option) => {
            const existing = this.colorData.find((item) => item.value === option);
            return {
              value: option,
              color: existing?.color || this.defaultColor,
            };
          });
        }
      }
    }
  }

  /**
   * Close dialog without saving
   */
  public onNoClick(): void {
    this.dialogRef.close(0);
  }

  /**
   * Confirm and save changes
   */
  public confirmAdd(): void {
    if (!this.isFormValid()) {
      console.warn('Form is invalid, cannot save');
      return;
    }

    if (this.catchIndex === null) {
      console.error('Cannot update: catchIndex is null');
      this.dialogRef.close(0);
      return;
    }

    const updatedTableColumnDataRow: TableColumnDataRow = {
      catchindex: this.catchIndex,
      bindingName: this.bindingName,
      targetField: this.targetField,
      shape: { name: this.selectedShape },
      defaultColor: this.defaultColor,
      colorData: [...this.colorData], // Create a copy
    };

    try {
      this.dataService.updateRecord(updatedTableColumnDataRow);
      this.dialogRef.close(1);
    } catch (error) {
      console.error('Error updating record:', error);
      this.dialogRef.close(0);
    }
  }

  /**
   * Check if form is valid
   */
  public isFormValid(): boolean {
    return !!(this.bindingName && this.targetField && this.targetField.fieldType !== 'default' && this.selectedShape && this.catchIndex !== null);
  }

  /**
   * Check if any changes were made
   */
  public hasChanges(): boolean {
    if (!this.originalData) return false;

    return (
      this.bindingName !== this.originalData.bindingName ||
      this.targetField?.name !== this.originalData.targetField?.name ||
      this.selectedShape !== this.originalData.shape?.name ||
      this.defaultColor !== this.originalData.defaultColor ||
      JSON.stringify(this.colorData) !== JSON.stringify(this.originalData.colorData)
    );
  }

  /**
   * Update default color and optionally apply to all color data items
   */
  public onDefaultColorChange(newColor: string): void {
    this.defaultColor = newColor;
  }

  /**
   * Update a specific color in the color data array
   */
  public onColorDataChange(index: number, newColor: string): void {
    if (this.colorData[index]) {
      this.colorData = this.colorData.map((item, i) => (i === index ? { ...item, color: newColor } : item));
    }
  }

  /**
   * Reset form to original values
   */
  public resetForm(): void {
    if (this.originalData) {
      this.bindingName = this.originalData.bindingName;
      this.targetField = this.originalData.targetField;
      this.selectedShape = this.originalData.shape?.name || '';
      this.defaultColor = this.originalData.defaultColor;
      this.colorData = [...this.originalData.colorData];

      if (this.form) {
        this.form.patchValue({
          bindingName: this.bindingName,
          targetField: this.targetField,
          selectedShape: this.selectedShape,
          defaultColor: this.defaultColor,
        });
      }
    }
  }

  /**
   * Get available fields excluding current one if it's being changed
   */
  public getAvailableFields(): TargetField[] {
    // In edit mode, show all fields including the current one
    return this.fieldList;
  }

  /**
   * Get title for the dialog
   */
  public getDialogTitle(): string {
    return `Edit Property Binding${this.bindingName ? `: ${this.bindingName}` : ''}`;
  }
}
